import React, { useState } from 'react';
import { offeredCategories, type CustomerCategory, type UsageIntent } from '@lds-efsp/shared-contracts';
import { useBrand } from '../../../app/config/brand';
import { Icons } from '../../../shared/icons/Icons';
import { jobRoles, allStates } from '../../../shared/data/formData';
import { useOnboardingApi } from '../../../api';
import { Button } from '../../../shared/components/ui/Button';
import { ChoiceGroup } from '../../../shared/components/ui/ChoiceGroup';

interface ProgressIndicatorProps {
  step: number;
  totalSteps: number;
  brand: { colors: { primary: string; onPrimary: string; border: string; textMuted: string } };
}

const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({ step, totalSteps, brand }) => (
  <div className="flex items-center justify-center mb-8">
    {Array.from({ length: totalSteps }, (_, i) => i + 1).map((s, i) => (
      <React.Fragment key={s}>
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-all"
          style={{
            backgroundColor: step >= s ? brand.colors.primary : brand.colors.border,
            color: step >= s ? brand.colors.onPrimary : brand.colors.textMuted,
          }}
        >
          {step > s ? <Icons.Check className="w-5 h-5" /> : s}
        </div>
        {i < totalSteps - 1 && (
          <div className="w-20 h-1 mx-2" style={{ backgroundColor: step > s ? brand.colors.primary : brand.colors.border }} />
        )}
      </React.Fragment>
    ))}
  </div>
);

interface OnboardingPageProps {
  user?: { name?: string; email?: string };
  onComplete: () => void;
  onLogout: () => void;
}

const OnboardingPage: React.FC<OnboardingPageProps> = ({ user, onComplete, onLogout }) => {
  const brand = useBrand();
  const { completeOnboarding, loading, error } = useOnboardingApi();
  const [step, setStep] = useState(1);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const pendingData = React.useMemo(() => {
    try {
      const stored = localStorage.getItem('pendingRegistration');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, []);

  const nameParts = (user?.name || '').split(' ');
  const defaultFirstName = pendingData?.firstName || nameParts[0] || '';
  const defaultLastName = pendingData?.lastName || nameParts.slice(1).join(' ') || '';

  // Category carried from the registration wizard's handoff (SOR-140). This
  // page has no category UI — it is deliberately kept OUT of formData so the
  // wholesale POST spread can never leak an intent-incompatible value;
  // offered-ness is re-checked against the finally-submitted usageType at
  // submit time.
  const carriedCategory: CustomerCategory | '' = pendingData?.customerCategory || '';

  // Legal acceptance evidence carried from the registration wizard's handoff
  // (SOR-37). Like the category: no UI on this page and deliberately kept out
  // of formData so the wholesale POST spread can never mint or edit one. It
  // forwards only as a complete pair — a half-pair is dropped, which the API
  // records as never-accepted (the account is then asked once when the first
  // published material version lands).
  const carriedAcceptance = (() => {
    const termsVersion = pendingData?.termsVersionAccepted;
    const privacyVersion = pendingData?.privacyVersionAccepted;
    if (typeof termsVersion !== 'number' || termsVersion < 1) return null;
    if (typeof privacyVersion !== 'number' || privacyVersion < 1) return null;
    return { termsVersionAccepted: termsVersion, privacyVersionAccepted: privacyVersion };
  })();

  const [formData, setFormData] = useState({
    usageType: pendingData?.usageType || '',
    firstName: defaultFirstName,
    lastName: defaultLastName,
    phone: pendingData?.phone || '',
    jobRole: pendingData?.jobRole || '',
    barNumber: pendingData?.barNumber || '',
    stateOfRecord: pendingData?.stateOfRecord || '',
    accountType: pendingData?.accountType || 'new',
    customerNumber: pendingData?.customerNumber || '',
    billingZip: pendingData?.billingZip || '',
    firmName: pendingData?.firmName || '',
    address1: pendingData?.address1 || '',
    address2: pendingData?.address2 || '',
    city: pendingData?.city || '',
    state: pendingData?.state || '',
    zip: pendingData?.zip || '',
  });

  const updateField = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const isFirm = formData.usageType === 'firm';

  const isStep1Valid = () => {
    if (!formData.usageType) return false;
    if (!formData.firstName || !formData.lastName || !formData.phone) return false;
    if (isFirm) {
      if (!formData.jobRole) return false;
      if (formData.jobRole === 'attorney' && (!formData.barNumber || !formData.stateOfRecord)) return false;
      if (formData.accountType === 'join' && (!formData.customerNumber || !formData.billingZip)) return false;
    }
    return true;
  };

  const isStep2Valid = () => {
    if (isFirm && !formData.firmName) return false;
    if (!formData.address1 || !formData.city || !formData.state || !formData.zip) return false;
    return true;
  };

  const handleNext = () => { if (step === 1) setStep(2); };
  const handleBack = () => { if (step === 2) setStep(1); };

  const handleSubmit = async () => {
    setSubmitError(null);
    // Include the carried category exactly when it is offered under the
    // finally-submitted usage intent — an intent change on this page that
    // makes it un-offered drops it from the payload entirely, so a persisted
    // category is always intent-compatible (SOR-140).
    const categoryOffered =
      carriedCategory !== '' &&
      (formData.usageType === 'firm' || formData.usageType === 'self') &&
      offeredCategories(formData.usageType as UsageIntent).includes(carriedCategory);
    const payload = {
      ...(categoryOffered ? { ...formData, customerCategory: carriedCategory } : formData),
      // Acceptance evidence rides the same completion POST (the provisioning
      // write happens server-side on this call).
      ...(carriedAcceptance ?? {}),
    };
    try {
      await completeOnboarding(payload);
      localStorage.removeItem('pendingRegistration');
      onComplete();
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to complete onboarding');
    }
  };

  return (
    <div className="min-h-screen py-6 sm:py-12 px-4" style={{ backgroundColor: brand.colors.bgLight }}>
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold mb-2" style={{ color: brand.colors.secondary }}>Complete Your Profile</h1>
          <p className="text-sm sm:text-base" style={{ color: brand.colors.textLight }}>Welcome, {user?.email}! Please complete your profile to continue.</p>
        </div>

        <ProgressIndicator step={step} totalSteps={2} brand={brand} />

        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-8">
          {(submitError || error) && (
            <div className="mb-6 p-4 rounded-lg" style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}>
              {submitError || error}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold" style={{ color: brand.colors.secondary }}>Account Information</h2>

              <div>
                <span className="block text-sm font-medium mb-3" style={{ color: brand.colors.text }}>How will you be using this account?</span>
                <div className="grid grid-cols-2 gap-4">
                  <button type="button" onClick={() => updateField('usageType', 'firm')} className={`p-4 rounded-lg border-2 text-left transition-all ${formData.usageType === 'firm' ? 'border-blue-500' : ''}`} style={{ borderColor: formData.usageType === 'firm' ? brand.colors.primary : brand.colors.border }}>
                    <Icons.Building className="w-6 h-6 mb-2" style={{ color: brand.colors.link }} />
                    <div className="font-medium" style={{ color: brand.colors.text }}>Law Firm / Organization</div>
                    <div className="text-sm" style={{ color: brand.colors.textLight }}>For attorneys and legal staff</div>
                  </button>
                  <button type="button" onClick={() => updateField('usageType', 'self')} className={`p-4 rounded-lg border-2 text-left transition-all ${formData.usageType === 'self' ? 'border-blue-500' : ''}`} style={{ borderColor: formData.usageType === 'self' ? brand.colors.primary : brand.colors.border }}>
                    <Icons.User className="w-6 h-6 mb-2" style={{ color: brand.colors.link }} />
                    <div className="font-medium" style={{ color: brand.colors.text }}>Self-Represented</div>
                    <div className="text-sm" style={{ color: brand.colors.textLight }}>Filing on your own behalf</div>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div><label htmlFor="onboarding-firstName" className="block text-sm font-medium mb-1" style={{ color: brand.colors.text }}>First Name *</label><input id="onboarding-firstName" type="text" value={formData.firstName} onChange={(e) => updateField('firstName', e.target.value)} className="w-full p-3 border rounded-lg" style={{ borderColor: brand.colors.border }} /></div>
                <div><label htmlFor="onboarding-lastName" className="block text-sm font-medium mb-1" style={{ color: brand.colors.text }}>Last Name *</label><input id="onboarding-lastName" type="text" value={formData.lastName} onChange={(e) => updateField('lastName', e.target.value)} className="w-full p-3 border rounded-lg" style={{ borderColor: brand.colors.border }} /></div>
              </div>

              <div><label htmlFor="onboarding-phone" className="block text-sm font-medium mb-1" style={{ color: brand.colors.text }}>Phone *</label><input id="onboarding-phone" type="tel" value={formData.phone} onChange={(e) => updateField('phone', e.target.value)} className="w-full p-3 border rounded-lg" style={{ borderColor: brand.colors.border }} /></div>

              {isFirm && (
                <>
                  <div><label htmlFor="onboarding-jobRole" className="block text-sm font-medium mb-1" style={{ color: brand.colors.text }}>Job Role *</label><select id="onboarding-jobRole" value={formData.jobRole} onChange={(e) => updateField('jobRole', e.target.value)} className="w-full p-3 border rounded-lg" style={{ borderColor: brand.colors.border }}><option value="">Select role...</option>{jobRoles.map(role => (<option key={role.value} value={role.value}>{role.label}</option>))}</select></div>
                  {formData.jobRole === 'attorney' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div><label htmlFor="onboarding-barNumber" className="block text-sm font-medium mb-1" style={{ color: brand.colors.text }}>Bar Number *</label><input id="onboarding-barNumber" type="text" value={formData.barNumber} onChange={(e) => updateField('barNumber', e.target.value)} className="w-full p-3 border rounded-lg" style={{ borderColor: brand.colors.border }} /></div>
                      <div><label htmlFor="onboarding-stateOfRecord" className="block text-sm font-medium mb-1" style={{ color: brand.colors.text }}>State of Record *</label><select id="onboarding-stateOfRecord" value={formData.stateOfRecord} onChange={(e) => updateField('stateOfRecord', e.target.value)} className="w-full p-3 border rounded-lg" style={{ borderColor: brand.colors.border }}><option value="">Select state...</option>{allStates.map(state => (<option key={state.value} value={state.value}>{state.label}</option>))}</select></div>
                    </div>
                  )}
                  <ChoiceGroup question="Account Type">
                    <div className="space-y-2">
                      <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer" style={{ borderColor: formData.accountType === 'new' ? brand.colors.primary : brand.colors.border }}><input type="radio" name="accountType" checked={formData.accountType === 'new'} onChange={() => updateField('accountType', 'new')} aria-label="Create New Firm Account" /><div><div className="font-medium" style={{ color: brand.colors.text }}>Create New Firm Account</div><div className="text-sm" style={{ color: brand.colors.textLight }}>Set up a new firm account</div></div></label>
                      <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer" style={{ borderColor: formData.accountType === 'join' ? brand.colors.primary : brand.colors.border }}><input type="radio" name="accountType" checked={formData.accountType === 'join'} onChange={() => updateField('accountType', 'join')} aria-label="Join Existing Firm" /><div><div className="font-medium" style={{ color: brand.colors.text }}>Join Existing Firm</div><div className="text-sm" style={{ color: brand.colors.textLight }}>Join your firm&apos;s existing account</div></div></label>
                    </div>
                  </ChoiceGroup>
                  {formData.accountType === 'join' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div><label htmlFor="onboarding-customerNumber" className="block text-sm font-medium mb-1" style={{ color: brand.colors.text }}>Customer Number *</label><input id="onboarding-customerNumber" type="text" value={formData.customerNumber} onChange={(e) => updateField('customerNumber', e.target.value)} className="w-full p-3 border rounded-lg" style={{ borderColor: brand.colors.border }} /></div>
                      <div><label htmlFor="onboarding-billingZip" className="block text-sm font-medium mb-1" style={{ color: brand.colors.text }}>Billing Zip *</label><input id="onboarding-billingZip" type="text" value={formData.billingZip} onChange={(e) => updateField('billingZip', e.target.value)} className="w-full p-3 border rounded-lg" style={{ borderColor: brand.colors.border }} /></div>
                    </div>
                  )}
                </>
              )}

              <div className="flex justify-between pt-4">
                <Button variant="secondary" onClick={onLogout}>Cancel</Button>
                <Button onClick={handleNext} disabled={!isStep1Valid()}>Continue</Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold" style={{ color: brand.colors.secondary }}>{isFirm ? 'Firm Information' : 'Your Address'}</h2>
              {isFirm && (<div><label htmlFor="onboarding-firmName" className="block text-sm font-medium mb-1" style={{ color: brand.colors.text }}>Firm Name *</label><input id="onboarding-firmName" type="text" value={formData.firmName} onChange={(e) => updateField('firmName', e.target.value)} className="w-full p-3 border rounded-lg" style={{ borderColor: brand.colors.border }} /></div>)}
              <div><label htmlFor="onboarding-address1" className="block text-sm font-medium mb-1" style={{ color: brand.colors.text }}>Address Line 1 *</label><input id="onboarding-address1" type="text" value={formData.address1} onChange={(e) => updateField('address1', e.target.value)} className="w-full p-3 border rounded-lg" style={{ borderColor: brand.colors.border }} /></div>
              <div><label htmlFor="onboarding-address2" className="block text-sm font-medium mb-1" style={{ color: brand.colors.text }}>Address Line 2</label><input id="onboarding-address2" type="text" value={formData.address2} onChange={(e) => updateField('address2', e.target.value)} className="w-full p-3 border rounded-lg" style={{ borderColor: brand.colors.border }} /></div>
              <div className="grid grid-cols-3 gap-4">
                <div><label htmlFor="onboarding-city" className="block text-sm font-medium mb-1" style={{ color: brand.colors.text }}>City *</label><input id="onboarding-city" type="text" value={formData.city} onChange={(e) => updateField('city', e.target.value)} className="w-full p-3 border rounded-lg" style={{ borderColor: brand.colors.border }} /></div>
                <div><label htmlFor="onboarding-state" className="block text-sm font-medium mb-1" style={{ color: brand.colors.text }}>State *</label><select id="onboarding-state" value={formData.state} onChange={(e) => updateField('state', e.target.value)} className="w-full p-3 border rounded-lg" style={{ borderColor: brand.colors.border }}><option value="">Select...</option>{allStates.map(state => (<option key={state.value} value={state.value}>{state.label}</option>))}</select></div>
                <div><label htmlFor="onboarding-zip" className="block text-sm font-medium mb-1" style={{ color: brand.colors.text }}>Zip *</label><input id="onboarding-zip" type="text" value={formData.zip} onChange={(e) => updateField('zip', e.target.value)} className="w-full p-3 border rounded-lg" style={{ borderColor: brand.colors.border }} /></div>
              </div>
              <div className="flex justify-between pt-4">
                <Button variant="secondary" onClick={handleBack}>Back</Button>
                <Button onClick={handleSubmit} disabled={!isStep2Valid() || loading}>{loading ? 'Submitting...' : 'Complete Setup'}</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
